import { lazy } from 'react';
import { Redirect } from 'react-router-dom';
import { getStepPath } from 'utils/bulk-import';

// Shared
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard.shared" */ 'pages/shared/All_Dashboard'));
const HomeRedirect = lazy(() => import(/* webpackChunkName: "home-redirect.shared" */ 'pages/shared/All_HomeRedirect'));
const NotificationPage = lazy(
  () => import(/* webpackChunkName: "notification-page.shared" */ 'pages/shared/NotificationContent')
);
const Producers = lazy(() => import(/* webpackChunkName: "producers.shared" */ 'pages/shared/Producers'));
const ProductListWithSelectedId = lazy(
  () => import(/* webpackChunkName: "product-list-selected.shared" */ 'pages/shared/ProductListWithSelectedId')
);
const Products = lazy(() => import(/* webpackChunkName: "products.shared" */ 'pages/shared/Products'));
const ProductAssistant = lazy(
  () => import(/* webpackChunkName: "product-assistant.shared" */ 'pages/shared/ProductAssistant')
);
const Insights = lazy(() => import(/* webpackChunkName: "insights.producer" */ 'pages/producer/Insights'));

// Admin
const AddProducer = lazy(() => import(/* webpackChunkName: "add-producer.notset" */ 'pages/NOTSET_AddProducer'));

// Logistician
const OrderDashboardPage = lazy(() => import(/* webpackChunkName: "order-dashboard" */ 'components/OrderDashboard'));
const OrderPage = lazy(() => import(/* webpackChunkName: "order-page" */ 'pages/Order'));
const ProducerProducts = lazy(
  () =>
    import(
      /* webpackChunkName: "producer-products.organization" */ 'pages/organization/Organization_ViewProducerProducts'
    )
);
const ShowProducer = lazy(
  () => import(/* webpackChunkName: "show-producer.shared" */ 'pages/organization/ShowProducer')
);

// Producer
const AddProduct = lazy(
  () => import(/* webpackChunkName: "add-product.shared" */ 'pages/producer/Producer_AddProduct')
);
const ProducerBulkImport = lazy(
  () => import(/* webpackChunkName: "bulk-import.producer" */ 'pages/producer/Producer_BulkImport')
);
const ProducerEditProduct = lazy(
  () => import(/* webpackChunkName: "edit-product.shared" */ 'pages/producer/Producer_EditProduct')
);
const ProducerIcaAssortmentOnlineProducts = lazy(
  () =>
    import(
      /* webpackChunkName: "ica-assortment-online.producer" */ 'pages/producer/ica-assortments/Producer_IcaAssortmentOnlineProducts'
    )
);
const ProducerIcaAssortmentProducts = lazy(
  () =>
    import(
      /* webpackChunkName: "ica-assortment.producer" */ 'pages/producer/ica-assortments/Producer_IcaAssortmentProducts'
    )
);
const ProducerOwnProducts = lazy(
  () => import(/* webpackChunkName: "own-products.producer" */ 'pages/producer/Producer_OwnProductsPage')
);
const ShowMyProfile = lazy(
  () => import(/* webpackChunkName: "my-profile.producer" */ 'pages/producer/Producer_MyPage')
);

// Store
// const StoreBulkImport = lazy(() => import(/* webpackChunkName: "bulk-import.store" */ 'pages/store/Store_BulkImport'));

// Superuser
const AllProductsPage = lazy(
  () => import(/* webpackChunkName: "all-products.superuser" */ 'pages/superuser/Superuser_AllProductsPage')
);
const BulkImport = lazy(
  () => import(/* webpackChunkName: "bulk-import.superuser" */ 'pages/superuser/Superuser_BulkImport')
);
const BulkImportNew = lazy(
  () => import(/* webpackChunkName: "bulk-import-new.superuser" */ 'pages/superuser/Superuser_BulkImportNew')
);
const ExportHistory = lazy(() => import(/* webpackChunkName: "export-history.superuser" */ 'pages/superuser/history'));
const SuperUserBulkReview = lazy(
  () => import(/* webpackChunkName: "bulk-review.superuser" */ 'pages/superuser/Superuser_BulkReview/BulkReview')
);
const SuperuserAllProducersPage = lazy(
  () => import(/* webpackChunkName: "all-producers.superuser" */ 'pages/superuser/Superuser_AllProducers')
);
const SuperuserApprovalForm = lazy(
  () => import(/* webpackChunkName: "approval-form.superuser" */ 'pages/superuser/Superuser_ApprovalForm')
);
const SuperuserCreateOrganization = lazy(
  () => import(/* webpackChunkName: "create-organization.superuser" */ 'pages/superuser/Superuser_CreateOrganization')
);
const SuperuserData = lazy(() => import(/* webpackChunkName: "data.superuser" */ 'pages/superuser/data'));
const SuperuserEditOrganization = lazy(
  () => import(/* webpackChunkName: "edit-organization.superuser" */ 'pages/superuser/Superuser_EditOrganization')
);
const SuperuserEditProducer = lazy(
  () => import(/* webpackChunkName: "edit-producer.shared" */ 'pages/superuser/Superuser_EditProducer')
);
const SuperuserErrors = lazy(() => import(/* webpackChunkName: "errors.superuser" */ 'pages/superuser/errors'));
const SuperuserExportProducts = lazy(
  () => import(/* webpackChunkName: "export-products.superuser" */ 'pages/superuser/Superuser_ExportProducts')
);
const SuperuserFailedProducts = lazy(
  () => import(/* webpackChunkName: "failed-products.superuser" */ 'pages/superuser/SuperuserFailedProducts')
);
const SuperuserIcaExportProducts = lazy(
  () => import(/* webpackChunkName: "ica-export.superuser" */ 'pages/superuser/Superuser_ICA_ExportProducts')
);
const SuperuserApoteaExportProducts = lazy(
  () => import(/* webpackChunkName: "apotea-export.superuser" */ 'pages/superuser/Superuser_Apotea_ExportProducts')
);
const SuperuserNotApprovedProducers = lazy(
  () =>
    import(/* webpackChunkName: "not-approved-producers.superuser" */ 'pages/superuser/Superuser_ProducersApprovalPage')
);
const SuperuserOrganizations = lazy(
  () => import(/* webpackChunkName: "organizations.superuser" */ 'pages/superuser/Superuser_Organizations')
);
const SuperuserProductsPage = lazy(
  () => import(/* webpackChunkName: "product-approval.superuser" */ 'pages/superuser/Superuser_ProductsApprovalPage')
);
const SuperuserWatermark = lazy(
  () => import(/* webpackChunkName: "watermark.superuser" */ 'pages/superuser/Superuser_Watermark')
);
const SuperuserWoltExportProducts = lazy(
  () => import(/* webpackChunkName: "wolt-export.superuser" */ 'pages/superuser/Superuser_Wolt_ExportProducts')
);
const SuperuserDatabase = lazy(
  () => import(/* webpackChunkName: "database.superuser" */ 'pages/superuser/Superuser_Database')
);
const SuperuserSettingsAi = lazy(
  () => import(/* webpackChunkName: "settings-ai.superuser" */ 'pages/superuser/settings')
);
const SuperuserStatistics = lazy(
  () => import(/* webpackChunkName: "statistics.superuser" */ 'pages/superuser/statistics')
);
// const TEMP_SuperuserDescSize = lazy(() => import(/* webpackChunkName: "temp-desc-size.superuser" */ 'pages/superuser/Superuser_TEMP_desc_size'));

export interface IRouteItem {
  path: string;
  getNavPath?: () => string;
  exact: boolean;
  component: any; // TODO any
}

export interface IRouteItems {
  [key: string]: IRouteItem;
}

export const ROUTES: IRouteItems = {
  homeRedirect: { path: '/', exact: true, component: HomeRedirect },
  dashboard: { path: '/dashboard', exact: true, component: Dashboard },
  productAssistant: { path: '/assistant', exact: true, component: ProductAssistant },
  insights: { path: '/insights', exact: true, component: Insights },
  me: { path: '/me', exact: true, component: ShowMyProfile },
  meProducts: {
    path: '/products',
    exact: true,
    component: ProducerOwnProducts,
  },
  // meBulkImport: {
  //     path: '/bulk-import',
  //     exact: true,
  //     component: ProducerBulkImport,
  // },
  meBulkImportNew: {
    path: '/bulk-import-new/:step',
    getNavPath: () => `/bulk-import-new/${getStepPath()}`,
    exact: false,
    component: ProducerBulkImport,
  },
  meShowProduct: {
    path: '/products/:id/show',
    exact: true,
    component: ProducerOwnProducts,
  },
  storeShowProduct: {
    path: '/me/products/:id/show',
    exact: true,
    component: ProducerOwnProducts,
  },
  meEditProduct: {
    path: '/products/:id/edit',
    exact: true,
    component: ProducerEditProduct,
  },
  storeEditProduct: {
    path: '/me/products/:id/edit',
    exact: true,
    component: ProducerEditProduct,
  },
  meEdit: {
    path: '/me/:id/edit',
    exact: true,
    component: SuperuserEditProducer,
  },
  producers: { path: '/producers', exact: true, component: Producers },
  showProducer: {
    path: '/producers/:id/show',
    exact: true,
    component: ShowProducer,
  },
  products: { path: '/products', exact: true, component: Products },
  storeProducts: { path: '/me/products', exact: true, component: ProducerOwnProducts },
  storeAllProducts: { path: '/products', exact: true, component: Products },
  // storeBulkImport: { path: '/bulk-import', exact: true, component: StoreBulkImport },
  showProduct: {
    path: '/products/:id/show',
    exact: true,
    component: ProductListWithSelectedId,
  },
  producerShowProduct: {
    path: '/producers/:producerId/products/:id/show',
    exact: true,
    component: ProducerProducts,
  },
  producerProducts: {
    path: '/producers/:id/products',
    exact: true,
    component: ProducerProducts,
  },
  producerData: {
    path: '/producers/data',
    exact: true,
    component: SuperuserData,
  },
  addProducer: {
    path: '/producers/addproducer',
    exact: true,
    component: AddProducer,
  },
  addProduct: {
    path: '/products/addproduct',
    exact: true,
    component: AddProduct,
  },
  storeAddProduct: {
    path: '/me/products/addproduct',
    exact: true,
    component: AddProduct,
  },
  orders: { path: '/orders', exact: true, component: OrderDashboardPage },
  orderStep: { path: '/order/:step', exact: false, component: OrderPage },
  orderNotification: {
    path: '/notification/:groupOrderNumber',
    exact: true,
    component: NotificationPage,
  },
  superuserProducts: {
    path: '/superuser/products',
    exact: true,
    component: SuperuserProductsPage,
  },
  // superuserTmp: {
  //   path: '/superuser/descriptivesize',
  //   exact: true,
  //   component: TEMP_SuperuserDescSize,
  // },
  superuserFailedProducts: {
    path: '/superuser/failed',
    exact: true,
    component: SuperuserFailedProducts,
  },
  superuserDatabase: {
    path: '/superuser/database/:tab?',
    getNavPath: () => '/superuser/database',
    exact: true,
    component: SuperuserDatabase,
  },
  superuserExportProducts: {
    path: '/superuser/export',
    exact: true,
    component: SuperuserExportProducts,
  },
  superuserIcaExportProducts: {
    path: '/superuser/ica-export',
    exact: true,
    component: SuperuserIcaExportProducts,
  },
  superuserWoltExportProducts: {
    path: '/superuser/wolt-export',
    exact: true,
    component: SuperuserWoltExportProducts,
  },
  superuserApoteaExportProducts: {
    path: '/superuser/apotea-export',
    exact: true,
    component: SuperuserApoteaExportProducts,
  },
  superuserExportNonFoodProducts: {
    path: '/superuser/export?nonfood=1',
    exact: true,
    component: SuperuserExportProducts,
  },
  superuserProducers: {
    path: '/superuser/producers',
    exact: true,
    component: SuperuserNotApprovedProducers,
  },
  superuserAllProductsList: {
    path: '/products',
    exact: true,
    component: AllProductsPage,
  },
  superuserOrganizations: {
    path: '/superuser/organizations',
    exact: true,
    component: SuperuserOrganizations,
  },
  superuserApprovalForm: {
    path: '/superuser/products/:id/approval',
    exact: true,
    component: (params: any) => {
      // remount every when [id] param changed
      return <SuperuserApprovalForm key={params?.match?.params?.id} {...params} />;
    },
  },
  superuserCreateOrganization: {
    path: '/superuser/addorganization',
    exact: true,
    component: SuperuserCreateOrganization,
  },
  superuserEditOrganization: {
    path: '/superuser/organization/:id/edit',
    exact: true,
    component: SuperuserEditOrganization,
  },
  superuserAllProducersList: {
    path: '/producers',
    exact: true,
    component: SuperuserAllProducersPage,
  },
  superuserEditProducer: {
    path: '/superuser/producer/:id/edit',
    exact: true,
    component: SuperuserEditProducer,
  },
  superuserAddProducer: {
    path: '/superuser/addproducer',
    exact: true,
    component: SuperuserEditProducer,
  },
  // The old Data page is the Coop GA tab of Statistics now.
  superuserData: {
    path: '/superuser/data',
    exact: true,
    component: () => <Redirect to="/superuser/statistics/coop-ga" />,
  },
  superuserErrors: {
    path: '/superuser/errors',
    exact: true,
    component: SuperuserErrors,
  },
  exportHistory: {
    path: '/superuser/export-history',
    exact: true,
    component: ExportHistory,
  },
  icaExportHistory: {
    path: '/superuser/export-history?type=ica',
    exact: true,
    component: ExportHistory,
  },
  bulkImport: {
    path: '/superuser/bulk-import',
    exact: true,
    component: BulkImport,
  },
  bulkImportNew: {
    path: '/superuser/bulk-import-new/:step',
    getNavPath: () => `/superuser/bulk-import-new/${getStepPath()}`,
    exact: false,
    component: BulkImportNew,
  },
  superuserBulkReviewQualityGroupReview: {
    path: '/superuser/bulk-review/:producerId/:qualityGroupId',
    exact: true,
    component: SuperUserBulkReview,
  },
  superuserBulkReviewQualityGroups: {
    path: '/superuser/bulk-review/:producerId',
    exact: true,
    component: SuperUserBulkReview,
  },
  superuserBulkReview: {
    path: '/superuser/bulk-review',
    exact: true,
    component: SuperUserBulkReview,
  },
  superuserWatermark: {
    path: '/superuser/wm',
    exact: true,
    component: SuperuserWatermark,
  },
  superuserSettingsAi: {
    path: '/superuser/settings/ai/:tab?',
    getNavPath: () => '/superuser/settings/ai',
    exact: true,
    component: SuperuserSettingsAi,
  },
  superuserStatistics: {
    path: '/superuser/statistics/:tab?',
    getNavPath: () => '/superuser/statistics',
    exact: true,
    component: SuperuserStatistics,
  },
  producerIcaAssortmentsProducts: {
    path: '/ica-assortments/products',
    exact: true,
    component: ProducerIcaAssortmentProducts,
  },
  producerIcaAssortmentsProductsOnline: {
    path: '/ica-assortments/products-online',
    exact: true,
    component: ProducerIcaAssortmentOnlineProducts,
  },
};
